import { Container, FilterButtonStyled, FilterChips, FilterSelectMenu } from '@components/Filter';
import { FilterButton } from '@components/Filter/FilterButton';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { FlexRow } from '@components/FlexRow';
import InputCheckboxGroup from '@components/Input/InputCheckboxGroup';
import InputSliderRangeStatic from '@components/Input/InputSliderRange/static';
import InputAutocomplete from '@components/InputAutocomplete';
import InputDateRange from '@components/InputDateRange';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import { useAuth } from '@contexts/AuthContext';
import {
    Box,
    Button,
    FormControlLabel,
    FormGroup,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { hasValue } from '../../components/Filter/filter.utils';
import { StatsModal } from '../jobApplications/StatsModal';
import { useGetJobOfferTemplates } from '../JobOfferTemplates/hooks/useGetJobOfferTemplates';
import { AutoCompleteAssignees } from './components/AutoCompleteAssignees';
import { useGetJobOfferAvailableFilters } from './hooks/useGetJobOfferAvailableFilters';

const ButtonCustom = styled(Button)(({ theme }) => ({
    background: 'none',
    width: '100%',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    padding: '12px 24px',
    maxHeight: '48px',
    borderRadius: '24px',
}));

export type IJobOfferFilter = {
    proId?: string[];
    jobApplicationStatus?: string[];
    salary?: {
        min: number | null;
        max: number | null;
    };
    city?: string[];
    workingHours?: {
        min: number | null;
        max: number | null;
    };
    activity?: string;
    experience?: {
        min: number | null;
        max: number | null;
    };
    experienceTypes?: {
        allYears: boolean;
        freshGraduate: boolean;
    };
    language?: string;
    brandId?: string[];
    createdAt?: {
        start: string;
        end: string;
    };
    publishDate?: {
        start: string;
        end: string;
    };
    jobApplicationType?: string;
    activity2?: string;
    professions?: string[];
    hardSkills?: string[];
    createdBy?: string[];
    position?: {
        professions?: string[];
    };
    candidate?: {
        languageIds?: string[];
        softSkillIds?: string[];
        specialties?: string[];
    };
    distance?: {
        distance: number | null;
        location: {
            lat: number | null;
            lng: number | null;
        } | null;
    };
    sectors: string[];
    status?: string;
    isHidden?: boolean | null;
    assigneeIds?: string[];
    upercutAssigneeIds?: string[];
};

export const defaultFilters: IJobOfferFilter = {
    createdAt: {
        start: null,
        end: null,
    },
    publishDate: {
        start: null,
        end: null,
    },
    activity: '',
    sectors: [],
    proId: [],
    brandId: [],
    salary: {
        min: null,
        max: null,
    },
    city: [],
    workingHours: {
        min: null,
        max: null,
    },
    jobApplicationStatus: [],
    jobApplicationType: '',
    experience: {
        min: null,
        max: null,
    },
    experienceTypes: {
        allYears: null,
        freshGraduate: null,
    },
    activity2: '',
    professions: [],
    hardSkills: [],
    createdBy: [],
    position: {
        professions: [],
    },
    candidate: {
        languageIds: [],
        softSkillIds: [],
        specialties: [],
    },
    distance: {
        distance: null,
        location: null,
    },
    status: 'ACTIVE',
    isHidden: null,
    assigneeIds: [],
    upercutAssigneeIds: [],
};

export interface IJobOfferFilterOption {
    value: string;
    label: string;
    count: number;
    groupLabel?: string;
}

export interface IJobOfferAssigneeOption {
    value: string;
    label: string;
    count: number;
    email?: string;
    avatarUrl?: string;
    assigneeType?: string;
}

export interface IJobOfferAvailableFilters {
    sectors: IJobOfferFilterOption[];
    professionIds: IJobOfferFilterOption[];
    specialtyIds: IJobOfferFilterOption[];
    contractTypes: IJobOfferFilterOption[];
    languageIds: IJobOfferFilterOption[];
    cities: IJobOfferFilterOption[];
    status: IJobOfferFilterOption[];
    proIds: IJobOfferFilterOption[];
    brandIds: IJobOfferFilterOption[];
    salaryRange: { min: number; max: number } | null;
    workingHoursRange: { min: number; max: number } | null;
    assignees: IJobOfferAssigneeOption[];
    upercutAssignees: IJobOfferAssigneeOption[];
}

export const defaultJobOfferAvailableFilters: IJobOfferAvailableFilters = {
    sectors: [],
    professionIds: [],
    specialtyIds: [],
    contractTypes: [],
    languageIds: [],
    cities: [],
    status: [],
    proIds: [],
    brandIds: [],
    salaryRange: null,
    workingHoursRange: null,
    assignees: [],
    upercutAssignees: [],
};

export const statusLabels = {
    ACTIVE: 'En cours',
    INACTIVE: 'Fermée',
    EXPIRED: 'Expirée',
    UNPUBLISHED: 'À venir',
    DRAFT: 'Brouillon',
};

export const filterLabels = {
    activity: 'Activité',
    jobApplicationStatus: 'Statut de la candidature',
    createdAt: 'Date de création',
    publishDate: 'Date de publication',
    sectors: 'Secteur d’activité',
    proId: 'Filtrer par entreprise',
    brandId: 'Marque',
    city: `CP, Ville, département, région`,
    distance: `Distance du point de départ`,
    workingHours: 'Heures de travail/semaine',
    jobApplicationType: 'Type de contrat',
    experience: 'Expérience',
    experienceTypes: "Types d'expérience",
    language: 'Langue',
    languageIds: 'Langue',
    activity2: 'Activité',
    salary: 'Salaire',
    professions: 'Fonction',
    softSkillIds: 'Savoir-être',
    hardSkills: 'Savoir-faire',
    createdBy: 'Créé par',
    specialties: 'Savoir-faire',
    status: 'Statut',
    isHidden: 'Visibilité',
    assigneeIds: 'Responsables',
    upercutAssigneeIds: 'Équipe Upercut',
};

export const JobOfferFilter = ({
    viewMode,
    totalDocs,
    changeViewMode,
    onQueryChange,
    onSortChange,
    onFilterChange,
    onAddJobOffer,
    filter,
    query = '',
    proId,
    brandId,
}: {
    viewMode: string;
    totalDocs: number;
    changeViewMode: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onQueryChange: (query: string) => void;
    onSortChange: (sort: { field: string; order: number }) => void;
    onFilterChange: (filter: IJobOfferFilter) => void;
    onAddJobOffer?: () => void;
    filter?: IJobOfferFilter;
    query?: string;
    proId?: string;
    brandId?: string;
}) => {
    const { isUpercut, user } = useAuth();
    const navigate = useNavigate();
    const { totalDocs: templateCount } = useGetJobOfferTemplates('', {
        proId: proId ? [proId] : undefined,
    });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);

    const lockedDefaults = useMemo<Partial<IJobOfferFilter>>(
        () => ({
            ...(proId ? { proId: [proId] } : {}),
            ...(brandId ? { brandId: [brandId] } : {}),
        }),
        [proId, brandId],
    );

    const lockedHideKeys = useMemo(
        () => [...(proId ? ['proId'] : []), ...(brandId ? ['brandId'] : [])],
        [proId, brandId],
    );
    const [chipLabels, setChipLabels] = useState<Record<string, any>>({
        sectors: [],
        proId: [],
        brandId: [],
        professions: [],
        languageIds: [],
        specialties: [],
        createdBy: [],
        assigneeIds: [],
        city: [],
        softSkillIds: [],
        hardSkills: [],
        jobApplicationStatus: [],
        status: Object.entries(statusLabels).map(([value, label]) => ({ label, id: value, value })),
    });

    const methods = useForm({
        defaultValues: { ...defaultFilters, ...lockedDefaults },
    });

    const handleFieldChange = () => {
        setTimeout(() => {
            onFilterChange(methods.getValues() as IJobOfferFilter);
        }, 500);
    };

    const [submittedFilters, setSubmittedFilters] = useState<IJobOfferFilter>(defaultFilters);
    const { availableFilters } = useGetJobOfferAvailableFilters(query, { ...submittedFilters, ...lockedDefaults });

    const toggleDrawer = open => () => {
        setIsDrawerOpen(open);
    };

    const onApplyFilter = data => {
        setSubmittedFilters(data);
        onFilterChange(data);
        setIsDrawerOpen(false);
    };

    const onResetFilter = () => {
        const resetValues = { ...defaultFilters, ...lockedDefaults };
        setSubmittedFilters(resetValues);
        onFilterChange(resetValues);
        methods.reset(resetValues);
        setIsDrawerOpen(false);
    };

    const handleStatsModalOpen = () => {
        setIsStatsModalOpen(true);
    };

    const handleStatsModalClose = () => {
        setIsStatsModalOpen(false);
    };

    useEffect(() => {
        setChipLabels(prev => ({
            ...prev,
            sectors: availableFilters.sectors.map(o => ({ label: o.label, id: o.value, value: o.value })),
            proId: availableFilters.proIds.map(o => ({ label: o.label, id: o.value, value: o.value })),
            brandId: availableFilters.brandIds.map(o => ({ label: o.label, id: o.value, value: o.value })),
            professions: availableFilters.professionIds.map(o => ({ label: o.label, id: o.value, value: o.value })),
            languageIds: availableFilters.languageIds.map(o => ({ label: o.label, id: o.value, value: o.value })),
            specialties: availableFilters.specialtyIds.map(o => ({ label: o.label, id: o.value, value: o.value })),
            city: availableFilters.cities.map(o => ({ label: o.label, id: o.value, value: o.value })),
            createdBy: availableFilters.proIds.map(o => ({ label: o.label, id: o.value, value: o.value })),
            assigneeIds: availableFilters.assignees.map(o => ({ label: o.label, id: o.value, value: o.value })),
            upercutAssigneeIds: availableFilters.upercutAssignees.map(o => ({
                label: o.label,
                id: o.value,
                value: o.value,
            })),
        }));
    }, [availableFilters]);

    return (
        <FormProvider {...methods}>
            <Container
                sx={{
                    marginBottom: '0px',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 1,
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1,
                        alignItems: { xs: 'space-between', md: 'center' },
                        width: '100%',
                    }}>
                    <FlexRow>
                        <SearchBarWithNumber
                            onChange={onQueryChange}
                            placeholder="Rechercher un talent"
                            totalCount={totalDocs}
                        />
                        <FilterButton
                            toggleDrawer={toggleDrawer}
                            hasActiveFilters={Object.entries(filter || {}).some(
                                ([key, value]) => !['status', ...lockedHideKeys].includes(key) && hasValue(value),
                            )}
                        />
                    </FlexRow>
                    <FilterSelectMenu
                        size="small"
                        value={filter?.status || defaultFilters.status}
                        onChange={event => {
                            const status = event.target.value as string;
                            onFilterChange({ ...filter, status });
                            methods.setValue('status', status);
                            onApplyFilter({ ...methods.getValues(), status });
                        }}>
                        {Object.entries(statusLabels).map(([value, label]) => {
                            const count = availableFilters.status.find(s => s.value === value)?.count;
                            return (
                                <MenuItem key={value} value={value}>
                                    {count !== undefined ? `${label} (${count})` : label}
                                </MenuItem>
                            );
                        })}
                    </FilterSelectMenu>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1,
                        alignItems: { xs: 'space-between', md: 'center' },
                        width: '100%',
                        justifyContent: 'end',
                    }}>
                    <FlexRow sx={{ justifyContent: { xs: 'space-between', md: 'flex-start' } }}>
                        {/*<ButtonViewContainer>
                            <ButtonView onClick={changeViewMode} value="list" activeView={viewMode === 'list'}>
                                <i className="fi-br-list" />
                            </ButtonView>
                            <ButtonView disabled onClick={changeViewMode} value="grid" activeView={viewMode === 'grid'}>
                                <i className="fi-br-grid" />
                            </ButtonView>
                            <ButtonView disabled onClick={changeViewMode} value="eye" activeView={viewMode === 'eye'}>
                                <i className="fi-br-eye" />
                            </ButtonView>
                        </ButtonViewContainer>*/}
                        <FilterButtonStyled onClick={handleStatsModalOpen}>
                            <i className="fi-br-stats" style={{ fontSize: '20px' }} />
                        </FilterButtonStyled>
                    </FlexRow>
                    <FlexRow sx={{ justifyContent: { xs: 'space-between', md: 'flex-start' } }}>
                        <ButtonCustom
                            onClick={() =>
                                navigate(proId ? `/companies/${proId}/job-offer-templates` : '/job-offer-templates')
                            }
                            sx={{
                                padding: '10px 24px',
                                background: 'none',
                                maxWidth: '141px',
                                border: theme => `2px solid ${theme.palette.border.main}`,
                            }}>
                            <Typography fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                                Modèles ({templateCount})
                            </Typography>
                        </ButtonCustom>
                        <Button variant="contained" color="primary" size="large" onClick={onAddJobOffer}>
                            <i className="fi-br-plus" style={{ marginRight: '8px' }} />
                            Ajouter
                        </Button>
                    </FlexRow>
                </Box>
            </Container>
            {Object.entries(filter || {}).some(
                ([key, value]) => !['status', ...lockedHideKeys].includes(key) && hasValue(value),
            ) && (
                <Container
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        '&::-webkit-scrollbar': {
                            height: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: theme => theme.palette.neutralDarkGray.main,
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: theme => theme.palette.neutralGray.main,
                        },
                    }}>
                    <FlexRow sx={{ width: '100%', minWidth: 0, gap: '10px' }}>
                        <FilterChips
                            filter={filter}
                            labels={filterLabels}
                            defaultFilters={defaultFilters}
                            onFilterChange={onFilterChange}
                            chipLabels={chipLabels}
                            stackProps={{ flexWrap: 'nowrap' }}
                            hideKeys={['status', ...lockedHideKeys]}
                            renderLabel={({ key, value, label }) => {
                                if (key === 'isHidden' && typeof value === 'boolean') {
                                    return `${label}: ${value ? 'Privé' : 'Non public'}`;
                                }
                                return undefined;
                            }}
                        />
                    </FlexRow>
                </Container>
            )}
            <FilterDrawer
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}>
                <FormGroup>
                    <FormLabel>{filterLabels.createdAt}</FormLabel>
                    <InputDateRange name="createdAt" onChange={handleFieldChange} />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.publishDate}</FormLabel>
                    <InputDateRange name="publishDate" onChange={handleFieldChange} />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.sectors}</FormLabel>
                    <InputAutocomplete
                        id="sectors"
                        name="sectors"
                        multiple
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.sectors}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                {isUpercut && !proId && !brandId && (
                    <FormGroup>
                        <FormLabel>{filterLabels.proId}</FormLabel>
                        <InputAutocomplete
                            id="proId"
                            name="proId"
                            multiple
                            getOptionLabel={option => `${option.label} (${option.count})`}
                            options={availableFilters.proIds}
                            onChange={handleFieldChange}
                        />
                    </FormGroup>
                )}
                {!brandId && (
                    <FormGroup>
                        <FormLabel>{filterLabels.brandId}</FormLabel>
                        <InputAutocomplete
                            id="brandId"
                            name="brandId"
                            multiple
                            getOptionLabel={option => `${option.label} (${option.count})`}
                            options={availableFilters.brandIds}
                            onChange={handleFieldChange}
                        />
                    </FormGroup>
                )}
                <FormGroup>
                    <FormLabel>{filterLabels.professions}</FormLabel>
                    <InputAutocomplete
                        id="professions"
                        name="position.professions"
                        multiple
                        groupBy={option => option.groupLabel}
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.professionIds}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.city}</FormLabel>
                    <InputAutocomplete
                        id="cities"
                        name="cities"
                        multiple
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.cities}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                {/* <FormGroup>
                    <FormLabel>{filterLabels.distance}</FormLabel>
                    <InputDistance name="distance" min={0} max={150} units="km" minLabel="0 km" maxLabel="150 km" onChange={handleFieldChange} />
                </FormGroup> */}

                <FormGroup>
                    <FormLabel>{filterLabels.salary}</FormLabel>
                    <InputSliderRangeStatic
                        name="salary"
                        min={availableFilters.salaryRange?.min ?? 0}
                        max={availableFilters.salaryRange?.max ?? 5000}
                        defaultMin={1000}
                        defaultMax={2000}
                        units="€"
                        minLabel="0€"
                        maxLabel={`${availableFilters.salaryRange?.max ?? 5000}€`}
                        minDistance={100}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.workingHours}</FormLabel>
                    <InputSliderRangeStatic
                        name="workingHours"
                        min={availableFilters.workingHoursRange?.min ?? 0}
                        max={availableFilters.workingHoursRange?.max ?? 60}
                        defaultMin={10}
                        defaultMax={30}
                        units="h"
                        minLabel="0h"
                        maxLabel={`${availableFilters.workingHoursRange?.max ?? 60} h`}
                        minDistance={1}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.experience}</FormLabel>
                    <InputSliderRangeStatic
                        name="experience"
                        min={0}
                        max={25}
                        defaultMin={3}
                        defaultMax={10}
                        units="y"
                        minLabel="0 year"
                        maxLabel="25 year"
                        minDistance={0}
                        onChange={handleFieldChange}
                    />
                    <InputCheckboxGroup
                        name="experienceTypes"
                        direction="row"
                        options={[
                            { label: 'Toute année', value: 'allYears' },
                            { label: 'Jeune diplômé', value: 'freshGraduate' },
                        ]}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.language}</FormLabel>
                    <InputAutocomplete
                        id="languageIds"
                        name="candidate.languageIds"
                        multiple
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.languageIds}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.specialties}</FormLabel>
                    <InputAutocomplete
                        id="specialties"
                        name="candidate.specialties"
                        multiple
                        groupBy={option => option.groupLabel}
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.specialtyIds}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                {isUpercut && (
                    <FormGroup>
                        <FormLabel>{filterLabels.createdBy}</FormLabel>
                        <InputAutocomplete
                            id="createdBy"
                            name="createdBy"
                            multiple
                            getOptionLabel={option => `${option.label} (${option.count})`}
                            options={availableFilters.proIds}
                            onChange={handleFieldChange}
                        />
                    </FormGroup>
                )}
                {isUpercut && (
                    <FormGroup>
                        <FormLabel>{filterLabels.isHidden}</FormLabel>
                        <Controller
                            name="isHidden"
                            control={methods.control}
                            render={({ field }) => (
                                <RadioGroup
                                    row
                                    value={field.value === true ? 'true' : field.value === false ? 'false' : ''}
                                    onChange={e => {
                                        const v = e.target.value;
                                        field.onChange(v === 'true' ? true : v === 'false' ? false : null);
                                        handleFieldChange();
                                    }}>
                                    <FormControlLabel value="" control={<Radio />} label="Tout voir" />
                                    <FormControlLabel value="true" control={<Radio />} label="Privé" />
                                    <FormControlLabel value="false" control={<Radio />} label="Public" />
                                </RadioGroup>
                            )}
                        />
                    </FormGroup>
                )}
                <FormGroup>
                    <FormLabel>{filterLabels.assigneeIds}</FormLabel>
                    <AutoCompleteAssignees
                        name="assigneeIds"
                        options={availableFilters.assignees}
                        onChange={handleFieldChange}
                    />
                </FormGroup>
                {isUpercut && (
                    <FormGroup>
                        <FormLabel>{filterLabels.upercutAssigneeIds}</FormLabel>
                        <AutoCompleteAssignees
                            name="upercutAssigneeIds"
                            options={availableFilters.upercutAssignees}
                            onChange={handleFieldChange}
                        />
                    </FormGroup>
                )}
            </FilterDrawer>

            {/* Stats Modal */}
            <StatsModal isStatsModalOpen={isStatsModalOpen} handleStatsModalClose={handleStatsModalClose} />
        </FormProvider>
    );
};
